import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: true, // HttpOnly 쿠키(rt) 전송 필수
});

// --- Access Token 저장/조회 (localStorage + 메모리 캐시) ---
let _accessToken = null;
let _userInfo = null;

// JWT payload를 파싱해서 만료 여부 확인 (라이브러리 없이)
// JWT는 base64url 인코딩이므로 atob 전에 표준 base64로 변환 필요
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return Date.now() / 1000 >= payload.exp;
  } catch {
    return true;
  }
}

// 앱 시작 또는 탭 복귀 시 호출
// 토큰이 만료됐거나, 토큰은 있는데 user 정보가 비어있으면(새 탭에서 localStorage 동기화가
// 늦게 반영되는 iOS WebKit 케이스 대응) refresh 시도. 토큰이 아예 없으면 비로그인 상태이므로 패스
export async function ensureAuth() {
  const token = getAccessToken();

  if (!token) {
    // 토큰이 전혀 없으면 명시적인 로그아웃 상태로 간주한다.
    return;
  }

  if (isTokenExpired(token) || !getUserInfo()?.name) {
    try {
      await refreshAuth();
    } catch {
      // refresh 실패는 doRefresh 내부에서 로그아웃 처리
    }
  }
}

export function setAccessToken(token) {
  _accessToken = token || null;
  if (token) localStorage.setItem('accessToken', token);
  else localStorage.removeItem('accessToken');
}

export function setUserInfo(user) {
  _userInfo = user || null;
  if (user) localStorage.setItem('user', JSON.stringify(user));
  else localStorage.removeItem('user');
}

export function getAccessToken() {
  // 다른 탭에서 갱신/삭제된 값을 메모리 캐시가 가리지 않도록 storage를 우선한다.
  if (typeof localStorage !== 'undefined') return localStorage.getItem('accessToken');
  return _accessToken;
}

export function getUserInfo() {
  if (typeof localStorage !== 'undefined') {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }
  return _userInfo || {};
}

// --- 요청 인터셉터: Authorization 헤더 부착 ---
api.interceptors.request.use((config) => {
  const at = getAccessToken();
  if (at) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${at}`;
  }
  return config;
});

// --- 응답 인터셉터: 401이면 한 번만 refresh, 동시요청 큐잉 ---
let isRefreshing = false;
let pendingQueue = []; // { resolve, reject, config }
let refreshPromise = null;

function notifyAuthRefreshed() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth:refreshed'));
  }
}

function refreshAuth() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function enqueueRequest(resolve, reject, config) {
  pendingQueue.push({ resolve, reject, config });
}

function resolveQueue(newToken) {
  pendingQueue.forEach(({ resolve, config }) => {
    config.headers = config.headers || {};
    if (newToken) config.headers.Authorization = `Bearer ${newToken}`;
    resolve(api(config));
  });
  pendingQueue = [];
}

function rejectQueue(error) {
  pendingQueue.forEach(({ reject }) => reject(error));
  pendingQueue = [];
}

async function doRefresh() {
  try {
    console.log('토큰 갱신 시도 중...');
    // rt 쿠키 기반 교환: 새 accessToken 반환(백엔드: POST /oauth/refresh)
    const res = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/oauth/refresh`, {}, {
      withCredentials: true,
      timeout: 10000 // 10초 타임아웃 설정
    });
    
    console.log('토큰 갱신 응답:', res.data);
    const newAt = res?.data?.accessToken;
    if (!newAt) {
      console.error('토큰 갱신 실패: accessToken이 응답에 없음');
      throw new Error('No accessToken in refresh response');
    }
    
    setAccessToken(newAt);
    setUserInfo(res?.data?.user || null);
    console.log('토큰 갱신 성공:', newAt);

    notifyAuthRefreshed();

    return newAt;
  } catch (error) {
    console.error('토큰 갱신 중 오류:', error);

    const status = error.response?.status;
    // RT 만료/무효(400) → 로컬 토큰만 지우고 홈으로
    // ⚠️ SAML SLO(logout())는 절대 자동 호출하지 않음
    //    자동 SLO 시 IdP SSO 세션까지 파기돼서 이후 로그인 시 loginFail로 빠짐
    if (status === 400 || status === 401 || status === 403) {
      // 새 탭에서 RT 쿠키만 늦게 준비되는 동안 유효한 AT까지 지우면 부분 초기화가
      // 완전 로그아웃으로 악화된다. AT가 실제 만료된 경우에만 로컬 세션을 제거한다.
      if (isTokenExpired(getAccessToken())) {
        setAccessToken(null);
        setUserInfo(null);
      }
    }

    throw error;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error || {};
    const status = response?.status;
    
    console.log('API 응답 에러:', {
      status,
      url: config?.url,
      isRefreshCall: config?.url?.includes('/oauth/refresh'),
      retry: config?._retry
    });

    // 무한루프 방지: refresh 자체가 401이면 바로 실패
    const isRefreshCall = config?.url?.includes('/oauth/refresh');

    if (status === 401 && !isRefreshCall && !config?._retry) {
      console.log('401 에러 감지, 토큰 갱신 시작');
      // 첫 401에서만 동작
      config._retry = true;

      if (isRefreshing) {
        console.log('이미 토큰 갱신 중, 큐에 추가');
        // 이미 갱신 중 → 큐에 넣고 대기
        return new Promise((resolve, reject) => enqueueRequest(resolve, reject, config));
      }

      try {
        isRefreshing = true;
        const newToken = await refreshAuth();

        // 대기 중인 요청들 재시도
        console.log(`토큰 갱신 완료, ${pendingQueue.length}개 대기 요청 재시도`);
        resolveQueue(newToken);

        // 현재 실패한 원 요청도 재시도
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        console.log('원래 요청 재시도:', config.url);
        return api(config);
      } catch (e) {
        console.error('토큰 갱신 실패:', e);
        // 갱신 실패 → 큐에 있던 것들 모두 실패 처리
        rejectQueue(e);
        setAccessToken(null);
        setUserInfo(null);
        
        // 로그인 페이지로 리다이렉트 (선택적)
        if (typeof window !== 'undefined') {
          console.log('토큰 갱신 실패로 인한 로그아웃 처리');
          // window.location.href = '/login';
        }
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// 토큰 없이 요청하는 public API 인스턴스
export const publicApi = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: false, // 쿠키 전송하지 않음
});

// 로그아웃 API 함수
export async function logout() {
  try {
    // dev 로그인은 SAML 세션이 없으므로 RT 쿠키만 지우는 로그아웃 경로를 사용한다.
    // 운영 로그인은 기존과 동일하게 SAML Single Logout을 수행한다.
    const isDevLogin = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'local';
    const logoutPath = isDevLogin ? '/oauth/sso/logout' : '/oauth/saml2/logout';
    window.location.href = `${process.env.VUE_APP_API_BASE_URL}${logoutPath}`;
  } catch (error) {
    console.error('Logout redirect failed:', error);
    // 리다이렉트 실패해도 클라이언트 토큰은 삭제
  } finally {
    // 토큰 삭제
    setAccessToken(null);
    setUserInfo(null);
  }
}
